import { useRef, useState, useEffect } from "react";
import { useLanguage } from "@/hooks/useLanguage";
import { Play } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Card } from "@/components/ui/card";

// Lazy YouTube component - loads iframe only when clicked
const LazyYouTubeVideo = ({ videoId, title }: { videoId: string; title: string }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  if (isLoaded) {
    return (
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 w-full h-full"
      />
    );
  }

  return (
    <button
      onClick={() => setIsLoaded(true)}
      className="absolute inset-0 w-full h-full cursor-pointer group"
      aria-label={`Play ${title}`}
    >
      <img
        src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
        alt={title}
        className="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
        <div className="bg-red-600 rounded-full p-4 group-hover:scale-110 transition-transform duration-300">
          <Play className="w-8 h-8 text-white fill-white" />
        </div>
      </div>
    </button>
  );
};

const videoTestimonials = [
  { id: "DQwk0T3THYw", title: "Client Testimonial 1" },
  { id: "dRgebvFg_Z4", title: "Client Testimonial 2" },
  { id: "zbFqyskpZv4", title: "Client Testimonial 3" },
  { id: "ZNdfV6HhUTA", title: "Client Testimonial 4" },
  { id: "rW7UO0x8qNI", title: "Client Testimonial 5" },
  { id: "UK7skYemY8w", title: "Client Testimonial 6" },
];

export const VideoTestimonialsSection = () => {
  const { t } = useLanguage();
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: "100px",
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 bg-gradient-to-b from-background to-muted/20">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            {t("home.videoTestimonials.title")}
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {t("home.videoTestimonials.subtitle")}
          </p>
        </div>

        {isVisible ? (
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full max-w-6xl mx-auto"
          >
            <CarouselContent className="-ml-4">
              {videoTestimonials.map((video, index) => (
                <CarouselItem key={video.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                  <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/50">
                    <AspectRatio ratio={16 / 9} className="bg-muted relative">
                      <LazyYouTubeVideo videoId={video.id} title={video.title} />
                    </AspectRatio>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex -left-12" />
            <CarouselNext className="hidden md:flex -right-12" />
          </Carousel>
        ) : (
          <div className="w-full max-w-6xl mx-auto h-64 bg-muted/20 rounded-lg animate-pulse" />
        )}
      </div>
    </section>
  );
};
